import { useRef, useState, useCallback } from "react";
import { Modal, ModalBody } from "reactstrap";
import { toPng } from "html-to-image";
import { getRateForOpp } from "../../utils/calculateVentas";

const MARCA = { Chile: "Wecast", Perú: "Visiona", Uruguay: "Visiona" };
const IMPUESTO = { Chile: "IVA", Perú: "IGV", Uruguay: "IVA" };
const MONEDA_TXT = { Chile: "pesos chilenos (CLP)", Perú: "soles (PEN)", Uruguay: "pesos uruguayos (UYU)" };
const MONEDA_TXT_USD = "dólares estadounidenses (USD)";

const fmt = (v, locale, dec) =>
  (v || 0).toLocaleString(locale, { maximumFractionDigits: dec, minimumFractionDigits: dec });

const pct = (v) => (v > 0 ? `${(v * 100).toFixed(1)}%` : "-");

const border = "1px solid #ccc";
const gris = "#f5f5f5";

const thStyle = {
  padding: "10px 12px",
  textAlign: "center",
  fontWeight: 700,
  fontSize: "0.88rem",
  color: "#fff",
  backgroundColor: "#222",
  border,
};

const labelTd = {
  padding: "7px 12px",
  fontWeight: 500,
  fontSize: "0.82rem",
  color: "#333",
  backgroundColor: gris,
  border,
  textAlign: "left",
};

const cellTd = {
  padding: "7px 12px",
  fontSize: "0.84rem",
  color: "#333",
  backgroundColor: "#fff",
  border,
  textAlign: "right",
};

const TIPO_MAP = { p: "Programmatic", d: "Digital", m: "Mixto" };

export const ComparadorModal = ({
  isOpen,
  toggle,
  oportunidades,
  cotizaciones,
  pais,
  viewInUSD = false,
  getDolarValue,
  fallbackRate = 1,
}) => {
  const ref = useRef(null);
  const [exporting, setExporting] = useState(false);

  const locale = { Chile: "es-CL", Perú: "es-PE", Uruguay: "es-UY" }[pais] || "es-CL";
  const sym = { Chile: "$", Perú: "S/", Uruguay: "$" }[pais] || "$";
  // Decimales: USD siempre 2; Perú local 2; Chile/Uruguay local 0
  const dec = viewInUSD ? 2 : { Chile: 0, Perú: 2, Uruguay: 0 }[pais] || 0;
  const marca = MARCA[pais] || "Wecast";
  const impuesto = IMPUESTO[pais] || "IVA";
  const monedaTxt = viewInUSD ? MONEDA_TXT_USD : MONEDA_TXT[pais] || "moneda local";

  // Factor para convertir un valor monetario crudo de la op a la moneda de display:
  //   - Si viewInUSD: dividir local por rate (=1 si la op ya es USD)
  //   - Si !viewInUSD: multiplicar USD por rate (=1 si la op ya es local)
  const getFactor = useCallback(
    (op) => {
      if (viewInUSD) {
        if (op.moneda_dolar) return 1;
        const rate = getDolarValue
          ? getRateForOpp({ ...op, moneda_dolar: true }, getDolarValue, fallbackRate)
          : fallbackRate;
        return rate > 0 ? 1 / rate : 0;
      }
      if (!op.moneda_dolar) return 1;
      const rate = getDolarValue ? getRateForOpp(op, getDolarValue, fallbackRate) : fallbackRate;
      return rate;
    },
    [viewInUSD, getDolarValue, fallbackRate]
  );

  const handleExportPNG = useCallback(async () => {
    if (!ref.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(ref.current, { backgroundColor: "#ffffff", pixelRatio: 2 });
      const a = document.createElement("a");
      a.download = `${marca} - Comparativo de propuestas para ${oportunidades[0]?.cliente || "Cliente"}.png`;
      a.href = dataUrl;
      a.click();
    } catch {
      // silent
    } finally {
      setExporting(false);
    }
  }, [oportunidades, marca]);

  if (!oportunidades || oportunidades.length < 2) return null;

  const cotMap = {};
  (cotizaciones || []).forEach((c) => {
    cotMap[c.codigo_generado] = c;
  });

  const data = oportunidades.map((op) => {
    const cot = cotMap[op.code] || {};
    const importeRaw = op.type === "btl" ? op.total_investment || 0 : op.importe || 0;
    // Factor de conversión a moneda de display (local o USD según viewInUSD)
    const factor = getFactor(op);
    const importe = importeRaw * factor;
    const mon = viewInUSD ? "US$" : op.moneda_dolar ? "US$" : sym;

    const edif = Number(op.edificiostotales || cot.edificios_unicos_total) || 0;
    const pant = Number(op.pantallastotales) || 0;
    const audiencia = Number(op.audiencia_total || cot.audiencia_seleccionada_total) || 0;
    const impactos = Number(op.impactos_totales || cot.impactos_totales) || 0;
    const salidas = Number(op.salidas_totales || cot.salidas_totales) || 0;

    const freq = op.formatopauta || cot.frecuencia || "-";
    const dias = op.periodopauta || cot.plazo || "-";
    const duracionVideo = Number(op.duracion || cot.duracion) || 0;
    const tipo = TIPO_MAP[op.tipo] || op.tipo || "Digital";

    // Horario
    let horario = "-";
    try {
      const opts = typeof op.options === "string" ? JSON.parse(op.options) : op.options;
      if (Array.isArray(opts) && opts.length > 1) {
        horario = opts
          .map((o) => {
            const m = o.label?.match(/(\d{2}:\d{2})\s*a\s*(\d{2}:\d{2})/);
            return m ? `${m[1]} - ${m[2]}` : o.label;
          })
          .join(", ");
      } else if (Array.isArray(opts) && opts.length === 1 && opts[0].label) {
        const match = opts[0].label.match(/(\d{2}:\d{2})\s*a\s*(\d{2}:\d{2})/);
        horario = match ? `${match[1]} - ${match[2]}` : opts[0].label.split("|")[0].trim();
      }
    } catch {
      // silent
    }

    // Inversión (todos los valores monetarios convertidos por factor)
    const tarifaFria = (Number(op.tarifa_fria_total || cot.tarifa_fria_total) || 0) * factor;
    const descPct = Number(op.descuento_pct || cot.descuento_total_final) || 0;
    const invBruta = tarifaFria > 0 ? tarifaFria : (Number(op.original_amount) || 0) * factor || importe;
    const invNeta = importe;

    // CPM y métricas (también convertidos por factor — son ratios monetarios)
    const cpmAudienciaRaw = Number(op.cpm_total || cot.cpm_total_final) || 0;
    const cpmAudiencia = cpmAudienciaRaw * factor;
    const cpmExhibicionRaw = Number(op.cpm_exhibicion) || (salidas > 0 ? (importeRaw / salidas) * 1000 : 0);
    const cpmExhibicion = cpmExhibicionRaw * factor;
    const valorPantalla = pant > 0 ? importe / pant : 0;

    return {
      id: op.id,
      campana: (op.campana || "-").toUpperCase(),
      mon,
      tipo,
      edif,
      pant,
      audiencia,
      impactos,
      salidas,
      freq,
      dias,
      duracionVideo,
      horario,
      invBruta,
      descPct,
      invNeta,
      cpmAudiencia,
      cpmExhibicion,
      valorPantalla,
      horasAlDia: Number(op.horas_pauta) || 0,
    };
  });

  const v = (fn) => data.map(fn);
  const money = (val, m) => (val > 0 ? `${m} ${fmt(val, locale, dec)}` : "-");

  const rows = [
    { label: "Formato", vals: v((d) => d.tipo) },
    { label: "Campaña", vals: v((d) => d.campana) },
    { label: "Edificios", vals: v((d) => d.edif), isNum: true },
    { label: "Pantallas", vals: v((d) => d.pant), isNum: true },
    { label: "Personas únicas", vals: v((d) => d.audiencia), isNum: true },
    { label: "Contactos", vals: v((d) => d.impactos), isNum: true },
    { label: "Visualizaciones", vals: v((d) => d.salidas), isNum: true },
    { label: "Duración video", vals: v((d) => (d.duracionVideo > 0 ? `${d.duracionVideo}s` : "-")) },
    { label: "Horario", vals: v((d) => d.horario) },
    { label: "Horas al día", vals: v((d) => (d.horasAlDia > 0 ? `${d.horasAlDia}h` : "-")) },
    { label: "Frecuencia", vals: v((d) => d.freq) },
    { label: "Días de pauta", vals: v((d) => d.dias) },
    { label: "Inversión bruta", vals: v((d) => d.invBruta), isMoney: true },
    { label: "Descuento", vals: v((d) => d.descPct), isPct: true },
    { label: "Inversión neta", vals: v((d) => d.invNeta), isMoney: true, bold: true, rowHi: true },
    { label: "CPM audiencia", vals: v((d) => d.cpmAudiencia), isMoney: true },
    { label: "CPM exhibición", vals: v((d) => d.cpmExhibicion), isMoney: true },
    { label: "Valor por pantalla", vals: v((d) => d.valorPantalla), isMoney: true },
  ];

  const colW = `${Math.floor(70 / data.length)}%`;
  const year = new Date().getFullYear();

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalBody style={{ padding: 0 }}>
        <div ref={ref} style={{ backgroundColor: "#fff", padding: "28px 32px 20px" }}>
          <div style={{ fontSize: "1.15rem", fontWeight: 700, color: "#1a1a1a", marginBottom: "16px" }}>
            Comparativo de propuestas para {oportunidades[0]?.cliente}
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: "left", width: "30%" }}>Opción</th>
                {data.map((d, i) => (
                  <th key={d.id} style={{ ...thStyle, width: colW }}>
                    {i + 1}
                  </th>
                ))}
              </tr>
              <tr>
                <td style={{ ...labelTd, fontWeight: 600 }}>ID</td>
                {data.map((d) => (
                  <td key={d.id} style={{ ...cellTd, backgroundColor: gris, fontWeight: 600 }}>
                    {d.id}
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => {
                const isHi = row.rowHi;
                return (
                  <tr key={ri}>
                    <td style={{ ...labelTd, backgroundColor: isHi ? "#e8f5e9" : gris }}>{row.label}</td>
                    {row.vals.map((val, ci) => {
                      let display;
                      if (row.isMoney) {
                        display = money(val, data[ci].mon);
                      } else if (row.isPct) {
                        display = pct(val);
                      } else if (row.isNum) {
                        display = val > 0 ? fmt(val, locale, 0) : "-";
                      } else {
                        display = val || "-";
                      }
                      return (
                        <td
                          key={ci}
                          style={{
                            ...cellTd,
                            fontWeight: row.bold ? 700 : 400,
                            fontSize: row.bold ? "0.88rem" : "0.84rem",
                            backgroundColor: isHi ? "#e8f5e9" : "#fff",
                          }}
                        >
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Footer legal */}
          <div style={{ marginTop: "14px", fontSize: "0.62rem", color: "#999", lineHeight: "1.5" }}>
            Propuesta exclusiva para agencia y/o cliente cotizado. Importes expresados en {monedaTxt} y no incluyen{" "}
            {impuesto}. Exhibición de lunes a viernes según frecuencia y horario contratado. Propuesta incluye
            descuentos especiales que no podrán considerarse como antecedente. Cotización válida para pautas a emitirse
            hasta diciembre {year} inclusive.
          </div>
        </div>

        {/* Botones fuera del ref */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", padding: "8px 32px 20px" }}>
          <button className="btn btn-outline-secondary btn-sm" onClick={toggle}>
            Cerrar
          </button>
          <button className="btn btn-dark btn-sm" onClick={handleExportPNG} disabled={exporting}>
            {exporting ? "Exportando..." : "Descargar PNG"}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};
